<template>
    <div>
        <div
            v-if="editing"
            class="container-fluid d-flex shadow py-2"
            style="border: 1px solid var(--primary-color); background: white"
        >
            <form class="form" @submit.prevent="submit">
                <div class="container-fluid">
                    <div class="row">
                        <SbFormGroup class="col-6">
                            <label for="name">Systemnamn</label>
                            <SbFormInput
                                id="name"
                                v-model="name"
                                type="text"
                                required
                                :readonly="isFixed"
                            />
                        </SbFormGroup>
                        <SbFormGroup class="col-6">
                            <label for="label">Fältnamn</label>
                            <SbFormInput
                                id="label"
                                v-model="label"
                                type="text"
                                required
                            />
                        </SbFormGroup>
                        <SbFormGroup class="col-6">
                            <label for="placeholder">Typ</label>
                            <SbFormSelect
                                id="type"
                                v-model="inputType"
                                required
                                :disabled="isFixed"
                            >
                                <SbFormSelectOption
                                    key="-1"
                                    :value="null"
                                    disabled
                                    >Select</SbFormSelectOption
                                >
                                <SbFormSelectOption
                                    v-for="(type, index) in inputTypes"
                                    :key="index"
                                    :value="type.value"
                                    >{{ type.label }}</SbFormSelectOption
                                >
                            </SbFormSelect>
                        </SbFormGroup>
                        <SbFormGroup class="col-6">
                            <label for="placeholder">Visningsnamn</label>
                            <SbFormInput
                                id="placeholder"
                                v-model="placeholder"
                                type="text"
                            />
                        </SbFormGroup>
                    </div>
                </div>
                <SbFormCheckbox v-model="enabled" name="check-button" switch>
                    Används
                    <i
                        v-bind:class="{ 'active-color': enabled }"
                        class="fal fa-square-check"
                    ></i>
                </SbFormCheckbox>
                <SbFormCheckbox v-model="hideForContact" switch>
                    Visa ej för kontakten
                    <i
                        v-bind:class="{ 'active-color': !hideForContact }"
                        class="fal fa-eye"
                    ></i>
                </SbFormCheckbox>
                <SbFormCheckbox v-model="required" switch>
                    Obilgatorisk för kontakten vid order
                    <i
                        v-bind:class="{ 'active-color': required }"
                        class="fal fa-octagon-exclamation"
                    ></i>
                </SbFormCheckbox>
                <SbFormCheckbox v-model="editableByContact" switch>
                    Editerbar för kontakten vid order
                    <i
                        v-bind:class="{ 'active-color': editableByContact }"
                        class="fal fa-pencil"
                    ></i>
                </SbFormCheckbox>
                <SbFormCheckbox v-model="sensitive" switch>
                    Innehåller känsliga personuppgifter
                    <i
                        v-bind:class="{ 'active-color': sensitive }"
                        class="fal fa-user-shield"
                    ></i>
                </SbFormCheckbox>

                <hr />

                <div class="d-flex" style="gap: 5px">
                    <button type="submit" class="btn btn-primary btn-sm">
                        Spara
                    </button>
                    <button
                        type="button"
                        class="btn btn-secondary btn-sm"
                        @click="$emit('cancelled')"
                    >
                        Avbryt
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<style scoped lang="scss">
.active-color {
    color: var(--primary-color);
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import SbFormCheckbox from "../sb/SbFormCheckbox.vue";
import SbFormGroup from "../sb/SbFormGroup.vue";
import SbFormInput from "../sb/SbFormInput.vue";
import SbFormSelect from "../sb/SbFormSelect.vue";
import SbFormSelectOption from "../sb/SbFormSelectOption.vue";

interface QuoteFieldAttribute {
    name?: string; label?: string; type?: string | null; fieldType?: string;
    enabled?: boolean; required?: boolean; placeholder?: string;
    hideForContact?: boolean; editableByContact?: boolean; sensitive?: boolean;
    [key: string]: unknown;
}

const props = defineProps<{ attribute: QuoteFieldAttribute; inputTypes?: unknown[] }>();
const emit = defineEmits<{ (event: "submitted", payload: QuoteFieldAttribute): void }>();
const name = ref<string>();
const label = ref<string>();
const inputType = ref<string | null>();
const fieldType = ref<string>();
const enabled = ref<boolean>();
const required = ref<boolean>();
const placeholder = ref<string>();
const hideForContact = ref<boolean>();
const editableByContact = ref<boolean>();
const sensitive = ref<boolean>();
const editing = ref(false);
const isFixed = computed(() => fieldType.value === "fixed");

onMounted(() => {
    ({ name: name.value, label: label.value, type: inputType.value, fieldType: fieldType.value,
        enabled: enabled.value, required: required.value, placeholder: placeholder.value,
        hideForContact: hideForContact.value, editableByContact: editableByContact.value,
        sensitive: sensitive.value } = props.attribute);
    editing.value = true;
});

function submit(): void {
    const payload = Object.assign(props.attribute, {
        name: name.value, label: label.value, type: inputType.value, fieldType: fieldType.value,
        enabled: enabled.value, required: required.value, placeholder: placeholder.value,
        hideForContact: hideForContact.value, editableByContact: editableByContact.value,
        sensitive: sensitive.value,
    });
    emit("submitted", payload);
    editing.value = false;
}
</script>
